import React, { useMemo } from 'react';
import {
  EChart,
  truncateLabel,
  getLabelWidth,
  getTruncateLength,
  getCompactGrid,
  getFocusCartesianGrid,
  getPreviewGrid,
} from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { EChartsOption } from 'echarts';

export interface WBSHotspotsChartProps {
  data: Array<{ wbs_id: string; wbs_short_name: string; wbs_name?: string; hotspotScore: number }>;
  height: number | string;
  onItemClick: (wbsId: string, wbsCode: string) => void;
  showDataLabels?: boolean;
  density?: ChartDensity;
}

export function WBSHotspotsChart({
  data,
  height,
  onItemClick,
  showDataLabels = true,
  density = 'overview',
}: WBSHotspotsChartProps) {
  const labelWidth = getLabelWidth(density);
  const truncLen = getTruncateLength(density);
  const isFocus = density === 'focus';
  const compactGrid = getCompactGrid(false);
  const previewGrid = getPreviewGrid(false);

  const option = useMemo<EChartsOption>(() => {
    const base: EChartsOption = {
      tooltip:
        density === 'preview'
          ? { show: false }
          : {
              trigger: 'axis',
              axisPointer: { type: 'shadow' },
              formatter: (params: unknown) => {
                const arr = Array.isArray(params) ? params : [params];
                const item = arr[0] as { dataIndex?: number; value?: number } | undefined;
                if (!item) return '';
                const row = data[item.dataIndex ?? 0];
                const fullName = row?.wbs_name || row?.wbs_short_name || row?.wbs_id || '';
                return `<div style="font-weight:600;max-width:280px;white-space:normal;word-break:break-word;">${fullName}</div><div>Score: ${item.value ?? 0}</div>`;
              },
            },
      grid:
        density === 'preview'
          ? previewGrid
          : isFocus
            ? getFocusCartesianGrid()
            : { ...compactGrid },
      xAxis: {
        type: 'value',
        name: 'Risk score',
        nameLocation: 'middle',
        nameGap: 24,
        axisLabel: {
          fontSize: density === 'preview' ? 9 : 10,
          ...(isFocus ? { formatter: (v: number) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }) } : {}),
        },
      },
      yAxis: {
        type: 'category',
        data: data.map((d) => truncateLabel(d.wbs_short_name || d.wbs_name || d.wbs_id, truncLen)),
        inverse: true,
        axisLabel: {
          fontSize: density === 'preview' ? 9 : 10,
          width: labelWidth,
          overflow: 'truncate',
          ellipsis: '…',
        } as { fontSize: number; width: number; overflow: 'truncate'; ellipsis: string },
      },
      series: [{
        type: 'bar',
        data: data.map((d) => d.hotspotScore),
        label: {
          show: density === 'preview' ? false : isFocus ? showDataLabels : showDataLabels && data.length <= 12,
          fontSize: 9,
          position: 'right',
          distance: 6,
        },
        labelLayout: { hideOverlap: true },
        barMaxWidth: 18,
        itemStyle: { color: '#3B82F6' },
      }],
    };
    return base;
  }, [data, showDataLabels, labelWidth, truncLen, density, isFocus, compactGrid, previewGrid]);

  const onEvents = useMemo(() => ({
    click: (params: { dataIndex?: number }) => {
      const row = data[params.dataIndex ?? 0];
      if (row) onItemClick(row.wbs_id, row.wbs_short_name || row.wbs_id);
    },
  }), [data, onItemClick]);

  return <EChart option={option} height={height} onEvents={onEvents} />;
}
